import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { downloadArchive } from '../lib/downloadArchive';
import { useT } from '../i18n';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Trash2,
  ExternalLink,
  ShieldCheck,
  Eye,
  Copy,
  Search,
  Upload,
  FileArchive,
  FileText,
  Download,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { XpnsrTableSkeleton } from './XpnsrSkeleton';

// White pages are the compliant, static pages served to bots / ad reviewers
// when a campaign cloaks. Unlike Landing pages / Pre-Landings / Offers (the
// operator's real funnels), a white page is hosted BY the tracker and served
// SAME-ORIGIN on the campaign's own domain (/wp/<slug>/) — so a cloaked Google
// Ads campaign keeps its final URL on-domain and passes "destination match".
// One uploaded white works across every attached domain.
export default function WhitePages() {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const { t } = useT();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewPage, setPreviewPage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [uploadForm, setUploadForm] = useState({ name: '', description: '', tags: '', file: null });
  const [uploading, setUploading] = useState(false);

  // Entry-point picker (shown when a ZIP has more than one HTML page)
  const [entryPickerOpen, setEntryPickerOpen] = useState(false);
  const [detectedPages, setDetectedPages] = useState([]);
  const [pendingUploadResult, setPendingUploadResult] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState('');

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/white-pages`);
      setPages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching white pages:', error);
      toast.error(t('Error loading white pages'));
    } finally {
      setLoading(false);
    }
  };

  // Uploaded bundles live on the backend origin (api host), not the frontend.
  const absUrl = (u) => (u && u.charAt(0) === '/' ? API.slice(0, API.lastIndexOf('/api')) + u : u);

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success(t('URL copied'));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && !file.name.endsWith('.zip')) {
      toast.error(t('Only ZIP files are supported'));
      return;
    }
    setUploadForm((prev) => ({ ...prev, file }));
  };

  const resetUploadForm = () => setUploadForm({ name: '', description: '', tags: '', file: null });

  const handleUpload = async (overrideIndexFile) => {
    if (!uploadForm.name.trim()) {
      toast.error(t('Please enter a name for the white page'));
      return;
    }
    if (!uploadForm.file) {
      toast.error(t('Please select a ZIP file'));
      return;
    }
    const fd = new FormData();
    fd.append('file', uploadForm.file);
    fd.append('name', uploadForm.name);
    if (uploadForm.description) fd.append('description', uploadForm.description);
    if (uploadForm.tags) fd.append('tags', uploadForm.tags);
    if (overrideIndexFile) fd.append('index_file', overrideIndexFile);

    try {
      setUploading(true);
      const response = await axios.post(`${API}/white-pages/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const returnedPages = response.data.pages || [];
      // Multiple HTML pages -> let the operator pick the entry page.
      if (returnedPages.length > 1 && !overrideIndexFile) {
        setDetectedPages(returnedPages);
        setSelectedEntry(response.data.index_file || returnedPages[0]);
        setPendingUploadResult(response.data);
        setUploadDialogOpen(false);
        setEntryPickerOpen(true);
        return;
      }
      const newPage = {
        id: response.data.id,
        name: response.data.name,
        url: response.data.url,
        slug: response.data.slug,
        description: uploadForm.description,
        tags: uploadForm.tags ? uploadForm.tags.split(',').map((s) => s.trim()) : [],
        is_uploaded: true,
        index_file: response.data.index_file,
        pages: response.data.pages || [],
        file_size: response.data.file_size,
        is_active: true
      };
      setPages([newPage, ...pages]);
      setUploadDialogOpen(false);
      setEntryPickerOpen(false);
      resetUploadForm();
      toast.success(t('White page uploaded successfully'));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || t('Error uploading white page'));
    } finally {
      setUploading(false);
    }
  };

  const handleEntryPointConfirm = async () => {
    if (!pendingUploadResult) return;
    // Replace the auto-created record with one using the chosen entry page.
    try {
      await axios.delete(`${API}/white-pages/${pendingUploadResult.id}`);
    } catch (_) { /* best effort */ }
    await handleUpload(selectedEntry);
    setPendingUploadResult(null);
    setDetectedPages([]);
  };

  const handleEntryPickerSkip = () => {
    if (pendingUploadResult) {
      const newPage = {
        id: pendingUploadResult.id,
        name: pendingUploadResult.name,
        url: pendingUploadResult.url,
        slug: pendingUploadResult.slug,
        description: uploadForm.description,
        tags: uploadForm.tags ? uploadForm.tags.split(',').map((s) => s.trim()) : [],
        is_uploaded: true,
        index_file: pendingUploadResult.index_file,
        pages: pendingUploadResult.pages || [],
        file_size: pendingUploadResult.file_size,
        is_active: true
      };
      setPages([newPage, ...pages]);
    }
    setEntryPickerOpen(false);
    setPendingUploadResult(null);
    setDetectedPages([]);
    resetUploadForm();
    toast.success(t('White page uploaded successfully'));
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/white-pages/${id}`);
      setPages(pages.filter((p) => p.id !== id));
      setDeleteTarget(null);
      toast.success(t('White page deleted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Error deleting white page'));
    }
  };

  const fmtSize = (b) => {
    if (!b && b !== 0) return '-';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  const filtered = pages.filter(
    (p) =>
      (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.url || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-8 space-y-6" data-testid="white-pages-skeleton">
        <XpnsrPageHeader kicker={t('Cloaking')} title={t('White pages')} subtitle={t('Static pages for bots & ad review')} />
        <XpnsrTableSkeleton rows={6} columns={4} />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="white-pages-view">
      <XpnsrPageHeader
        kicker={t('Cloaking')}
        title={
          <>
            {t('White pages')} <em>({pages.length})</em>
          </>
        }
        subtitle={t('Compliant static pages served to bots and ad reviewers — same-origin on your campaign domain.')}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/archive">{t('View archived')}</Link>
            </Button>
            <Dialog open={uploadDialogOpen} onOpenChange={(open) => { setUploadDialogOpen(open); if (!open) resetUploadForm(); }}>
              <DialogTrigger asChild>
                <Button onClick={resetUploadForm} data-testid="upload-white-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  {t('Upload ZIP')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{t('Upload white page')}</DialogTitle>
                  <DialogDescription>{t('Upload a static ZIP (index.html + assets). PHP is not allowed. Max 50MB.')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('Name')} *</Label>
                    <Input
                      value={uploadForm.name}
                      onChange={(e) => setUploadForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder={t('My white page')}
                      data-testid="white-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('Description')}</Label>
                    <Textarea
                      value={uploadForm.description}
                      onChange={(e) => setUploadForm((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder={t('What this white page is for')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('Tags')}</Label>
                    <Input
                      value={uploadForm.tags}
                      onChange={(e) => setUploadForm((prev) => ({ ...prev, tags: e.target.value }))}
                      placeholder={t('tag1, tag2, tag3')}
                    />
                    <p className="text-xs text-muted-foreground">{t('Separate tags with commas')}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('ZIP File')} *</Label>
                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                      <FileArchive className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-2">
                        {uploadForm.file ? uploadForm.file.name : t('Drag & drop or click to select')}
                      </p>
                      <input type="file" accept=".zip" onChange={handleFileChange} className="hidden" id="white-file-upload" />
                      <Button variant="outline" type="button" onClick={() => document.getElementById('white-file-upload').click()}>
                        {t('Select ZIP File')}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">{t('Static files only (.html/.css/.js/images). No .php.')}</p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
                    {t('Cancel')}
                  </Button>
                  <Button onClick={() => handleUpload()} disabled={uploading || !uploadForm.name || !uploadForm.file} data-testid="white-upload-submit">
                    {uploading ? t('Uploading...') : t('Upload')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" />
        <span>
          {t('These pages are hosted here and served same-origin at /wp/<slug>/ on any of your verified domains. Pick one in a campaign’s Cloaking settings — bots and Google/Facebook reviewers get this page, real users get the offer.')}
        </span>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          className="pl-10"
          placeholder={t('Search white pages…')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="white-search-input"
        />
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent style={{ padding: 0 }}>
            <XpnsrEmpty
              icon={ShieldCheck}
              title={searchQuery ? t('Nothing found') : t('No white pages')}
              subtitle={t('Upload a static white page to use for campaign cloaking')}
              cta={!searchQuery ? (
                <button type="button" className="xp-btn xp-btn-primary" onClick={() => setUploadDialogOpen(true)} data-testid="empty-upload-white">
                  <Upload style={{ width: 12, height: 12 }} /> {t('Upload ZIP')}
                </button>
              ) : null}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">{t('Name')}</TableHead>
                  <TableHead className="whitespace-nowrap">{t('Same-origin path')}</TableHead>
                  <TableHead className="whitespace-nowrap">{t('Pages')}</TableHead>
                  <TableHead className="text-right whitespace-nowrap">{t('Size')}</TableHead>
                  <TableHead className="text-right whitespace-nowrap">{t('Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((page) => (
                  <TableRow key={page.id} data-testid={`white-row-${page.id}`}>
                    <TableCell className="whitespace-nowrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{page.name}</span>
                          {page.pages?.length > 1 && (
                            <Badge variant="secondary" className="text-xs">{page.pages.length} {t('pages')}</Badge>
                          )}
                        </div>
                        {page.tags?.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {page.tags.slice(0, 3).map((tag) => (
                              <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <div className="flex items-center gap-1 max-w-[240px]">
                        <code className="truncate text-xs text-muted-foreground">{page.url}</code>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" aria-label={t('Copy path')} title={t('Copy path')} onClick={() => copyUrl(page.url)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {page.index_file || (page.pages && page.pages[0]) || '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium whitespace-nowrap">{fmtSize(page.file_size)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setPreviewPage(page)} aria-label={t('Preview')} title={t('Preview')}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" aria-label={t('Open')} title={t('Open')} onClick={() => window.open(absUrl(page.url), '_blank', 'noopener,noreferrer')} data-testid={`white-open-${page.id}`}>
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        {(page.is_uploaded || page.upload_path) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t('Download ZIP')}
                            title={t('Download ZIP')}
                            onClick={() => downloadArchive(API, `/white-pages/${page.id}/download`, page.name).catch(() => toast.error(t('Download failed')))}
                            data-testid={`download-${page.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" aria-label={t('Delete')} title={t('Delete')} onClick={() => setDeleteTarget(page)} data-testid={`white-delete-${page.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete white page')}
        description={t('This permanently deletes "{name}" and its files. Campaigns still pointing at it will fall back to their Safe page URL (or the offer if none).').replace('{name}', deleteTarget?.name || '')}
        confirmLabel={t('Delete')}
        variant="destructive"
        onConfirm={() => handleDelete(deleteTarget?.id)}
      />

      <Dialog open={!!previewPage} onOpenChange={(open) => { if (!open) setPreviewPage(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewPage?.name || t('Preview')}</DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              <span className="truncate">{previewPage?.url}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" aria-label={t('Open')} title={t('Open')} onClick={() => window.open(absUrl(previewPage?.url), '_blank', 'noopener,noreferrer')}>
                <ExternalLink className="w-3 h-3" />
              </Button>
            </DialogDescription>
          </DialogHeader>
          {previewPage?.url && (
            <div className="rounded-lg border overflow-hidden" style={{ height: '70vh' }}>
              <iframe src={absUrl(previewPage.url)} title={previewPage.name} className="w-full h-full" sandbox="allow-scripts allow-same-origin" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Entry-point picker (multi-page ZIP) */}
      <Dialog open={entryPickerOpen} onOpenChange={(open) => { if (!open) handleEntryPickerSkip(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('Choose entry point')}</DialogTitle>
            <DialogDescription>{t('Multiple HTML pages detected. Select the main page bots will land on.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[40vh] overflow-y-auto">
            {detectedPages.map((p) => (
              <label key={p} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedEntry === p ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}>
                <input type="radio" name="white-entry" value={p} checked={selectedEntry === p} onChange={() => setSelectedEntry(p)} className="accent-primary" />
                <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm truncate">{p}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleEntryPickerSkip}>{t('Skip (use auto-detected)')}</Button>
            <Button onClick={handleEntryPointConfirm} disabled={uploading}>
              {uploading ? t('Uploading...') : t('Confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
